import React, { createContext, useState, useEffect, useContext } from 'react';
import { notification } from 'antd';
import cartService from '../services/cartService';
import userRoleUtils from '../utils/userRoleUtils';
import eventBus from '../utils/eventBus';
import { clearCartOrderNotes } from '../utils/cartOrderNotes';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({});
  const [cartProperties, setCartProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [orderSchedule, setOrderSchedule] = useState({});
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const missingPropertyIds = Object.keys(cart).filter(id =>
    !cartProperties.some(property => String(property.id) === id)
  ).join('|');

  useEffect(() => {
    if (missingPropertyIds && !isModalOpen) loadCart();
  }, [missingPropertyIds, isModalOpen]);

  useEffect(() => {
    loadCart();
    
    // Load saved schedules from localStorage
    const savedSchedules = getScheduleFromLocalStorage();
    setOrderSchedule(savedSchedules);
    
    const unsubscribe = eventBus.on('cart:updated', (updatedCart) => {
      if (updatedCart) {
        setCart(updatedCart);
      }
    });
    
    const unsubscribeSchedule = eventBus.on('schedule:updated', (updatedSchedules) => {
      if (updatedSchedules) {
        setOrderSchedule(updatedSchedules);
      }
    });

    // Listen for modal state changes
    const unsubscribeModalOpen = eventBus.on('modal:open', () => {
      setIsModalOpen(true);
    });

    const unsubscribeModalClose = eventBus.on('modal:close', () => {
      setIsModalOpen(false);
    });
    
    return () => {
      unsubscribe();
      unsubscribeSchedule();
      unsubscribeModalOpen();
      unsubscribeModalClose();
    };
  }, []);

  useEffect(() => {
    const handleStorageChange = (e) => {
      // Don't update from storage events if modal is open
      if (isModalOpen) {
        return;
      }
      
      if (e.key === 'dateIsSet') {
        try {
          const newSchedules = JSON.parse(e.newValue || '{}');
          setOrderSchedule(prevSchedules => ({
            ...newSchedules,
            ...prevSchedules
          }));
        } catch (err) {
          console.error("Error parsing dateIsSet from storage event", err);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isModalOpen]);

  // ✅ SIMPLIFIED: Helper to get schedule from localStorage with validation
  const getScheduleFromLocalStorage = () => {
    try {
      const saved = localStorage.getItem('dateIsSet');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate that schedules have all required fields
        const validatedSchedules = {};
        Object.entries(parsed).forEach(([propertyId, schedule]) => {
          if (schedule && 
              schedule.order_date && 
              schedule.arrival_time && 
              schedule.completion_time &&
              schedule.order_date.trim() !== '' &&
              schedule.arrival_time.trim() !== '' &&
              schedule.completion_time.trim() !== '') {
            validatedSchedules[propertyId] = schedule;
          }
        });
        return validatedSchedules;
      }
    } catch (e) {
      console.error("Error parsing schedule from localStorage:", e);
    }
    return {};
  };

  // ✅ SIMPLIFIED: Helper to save schedule to localStorage
  const saveScheduleToLocalStorage = (propertyId, schedule) => {
    try {
      const existing = getScheduleFromLocalStorage();
      const updated = {
        ...existing,
        [propertyId]: schedule
      };
      localStorage.setItem('dateIsSet', JSON.stringify(updated));
      return true;
    } catch (e) {
      console.error("Error saving schedule to localStorage:", e);
      return false;
    }
  };

  const loadCart = async () => {
    // Don't load if modal is open
    if (isModalOpen) {
      console.log('Cart load prevented - modal is open');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const response = await cartService.getCart();
      
      if (response && response.cart) {
        setCart(response.cart);
      } else {
        setCart({});
      }
      
      if (response && response.properties) {
        setCartProperties(response.properties);
      } else {
        setCartProperties([]);
      }
    } catch (error) {
      console.error('Error loading cart:', error);
      setError('Failed to load cart data. Please try again.');
      notification.error({
        message: 'Cart Error',
        description: 'Could not load your cart. Please refresh the page.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ KEY FIX: Add to cart without auto-creating schedules
  const addToCart = async (propertyId) => {
    try {
      setIsLoading(true);
      
      const response = await cartService.addToCart(propertyId);
      
      if (response && response.status) {
        if (response.cart) setCart(response.cart);
        if (response.order_schedule) setOrderSchedule(response.order_schedule);
        
        // ✅ KEY FIX: Don't auto-create schedules
        // User must explicitly set date/time for each property
        
        notification.success({
          message: 'Added to Cart',
          description: 'Item added to cart. Please set date and time before checkout.',
          duration: 4
        });
        
        return true;
      } else {
        throw new Error(response?.message || 'Failed to add item to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      notification.error({
        message: 'Cart Error',
        description: error.message || 'Could not add item to cart.',
      });
      
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromCart = async (id, pid) => {
    try {
      setIsLoading(true);
      const propertyId = pid ?? id;
      const response = await cartService.removeFromCart(0, propertyId);
      
      if (response && response.status) {
        // Clear schedule data from localStorage for this property
        clearScheduleFromLocalStorage(propertyId);
        clearCartOrderNotes([propertyId]);
        
        setCart(prevCart => {
          const newCart = { ...prevCart };
          delete newCart[propertyId];
          return newCart;
        });
        
        setOrderSchedule(prevSchedule => {
          const newSchedule = { ...prevSchedule };
          delete newSchedule[propertyId];
          return newSchedule;
        });
        
        if (response.cart) setCart(response.cart);
        if (response.order_schedule) setOrderSchedule(response.order_schedule);
        
        notification.success({
          message: 'Removed from Cart',
          description: response.message || 'Item removed from cart successfully.',
        });
        
        return true;
      } else {
        throw new Error(response?.message || 'Failed to remove item from cart');
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
      notification.error({
        message: 'Cart Error',
        description: error.message || 'Could not remove item from cart.',
      });
      
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const updateCartItem = async (propertyId, itemData) => {
    try {
      setIsLoading(true);
      
      setCart(prevCart => {
        const newCart = { ...prevCart };
        
        if (newCart[propertyId]) {
          Object.entries(itemData).forEach(([itemId, item]) => {
            newCart[propertyId][itemId] = {
              ...newCart[propertyId][itemId],
              ...item
            };
          });
        }
        
        return newCart;
      });
      
      // Only refresh if modal is not open
      if (!isModalOpen) {
        await loadCart();
      }
      
      return true;
    } catch (error) {
      console.error('Error updating cart item:', error);
      notification.error({
        message: 'Cart Error',
        description: 'Could not update item in cart.',
      });
      
      // Only refresh if modal is not open
      if (!isModalOpen) {
        await loadCart();
      }
      
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ SIMPLIFIED: Update schedule with validation
  const updateSchedule = (propertyId, schedule) => {
    // Validate that all required fields are present
    if (!schedule.order_date || !schedule.arrival_time || !schedule.completion_time) {
      console.error('Incomplete schedule data:', schedule);
      return false;
    }
    
    // Save to context state
    setOrderSchedule(prevSchedule => ({
      ...prevSchedule,
      [propertyId]: schedule
    }));
    
    // Save to localStorage
    const saved = saveScheduleToLocalStorage(propertyId, schedule);
    
    if (saved) {
      // Only reload if modal is not open
      if (!isModalOpen) {
        loadCart();
      }
      eventBus.emit('schedule:updated', {
        ...orderSchedule,
        [propertyId]: schedule
      });
    }
    
    return saved;
  };

  const clearScheduleFromLocalStorage = (propertyId = null) => {
    try {
      if (propertyId) {
        // Clear specific property
        const existing = getScheduleFromLocalStorage();
        delete existing[propertyId];
        localStorage.setItem('dateIsSet', JSON.stringify(existing));
      } else {
        // Clear all schedules
        localStorage.removeItem('dateIsSet');
      }
      return true;
    } catch (e) {
      console.error("Error clearing schedule from localStorage:", e);
      return false;
    }
  };

  const clearCart = async () => {
    try {
      setIsLoading(true);
      
      clearScheduleFromLocalStorage();
      clearCartOrderNotes();
      
      setCart({});
      setCartProperties([]);
      setOrderSchedule({});
      
      return true;
    } catch (error) {
      console.error('Error clearing cart:', error);
      notification.error({
        message: 'Cart Error',
        description: 'Could not clear your cart.',
      });
      
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const getCartTotal = () => {
    if (!cart || Object.keys(cart).length === 0) return 0;

    let total = 0;
    
    Object.values(cart).forEach(items => {
      Object.values(items).forEach(item => {
        const price = parseFloat(item.price || 0);
        const quantity = parseFloat(item.quantity || item.qty || 0);
        total += price * quantity;
      });
    });
    
    return total;
  };

  const getCartItemCount = () => {
    if (!cart) return 0;
    return Object.keys(cart).length;
  };

  // ✅ SIMPLIFIED: Strict validation for all schedules being set
  const areAllSchedulesSet = () => {
    if (!cart || Object.keys(cart).length === 0) return false;
    
    // Check if we have a complete schedule for each property
    return Object.keys(cart).every(propertyId => {
      // First check the context's orderSchedule
      const scheduleInContext = orderSchedule[propertyId];
      if (scheduleInContext && 
          scheduleInContext.order_date && 
          scheduleInContext.arrival_time && 
          scheduleInContext.completion_time &&
          scheduleInContext.order_date.trim() !== '' &&
          scheduleInContext.arrival_time.trim() !== '' &&
          scheduleInContext.completion_time.trim() !== '') {
        return true;
      }
      
      // If not in context, check localStorage as fallback
      try {
        const savedSchedules = getScheduleFromLocalStorage();
        const schedule = savedSchedules[propertyId];
        return schedule && 
               schedule.order_date && 
               schedule.arrival_time && 
               schedule.completion_time &&
               schedule.order_date.trim() !== '' &&
               schedule.arrival_time.trim() !== '' &&
               schedule.completion_time.trim() !== '';
      } catch (e) {
        console.error("Error checking localStorage schedules", e);
        return false;
      }
    });
  };

  const contextValue = {
    cart,
    cartProperties,
    orderSchedule,
    isLoading,
    error,
    addToCart,
    removeFromCart,
    updateCartItem,
    updateSchedule,
    clearCart,
    getCartTotal,
    getCartItemCount,
    areAllSchedulesSet,
    refreshCart: loadCart,
    setIsModalOpen
  };

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  
  return context;
};

export default CartContext;
